import React from "react";
import { SliceComponentProps } from "@prismicio/react";
import { getImage, withArtDirection } from "gatsby-plugin-image";

import { AVAILABLE_LANGS } from "@typings/globals";

import { Button, Container, Row, Text } from "@ui/Atoms";
import { mediaQueries } from "@ui/themes/default";
import { Variant } from "@ui/Atoms/Button";

import { useTelemetry } from "@hooks/useTelemetry";

import {
  PrismicLandingPageV2DataBodyHerocalltoaction,
  PrismicLandingPageV2DataBodyHerocalltoactionItem,
} from "@www/graphql-types";

import { useProceedRedirect } from "../../LandingPageV2.helper";

import * as Styled from "./HeroCallToAction.styled";

export const HeroCallToAction = ({
  slice,
  context,
}: SliceComponentProps<
  PrismicLandingPageV2DataBodyHerocalltoaction,
  { language: AVAILABLE_LANGS; prismicPageId: string }
>) => {
  const primary = slice.primary;
  const items = slice.items;
  const redirect = useProceedRedirect();
  const { elementClick } = useTelemetry();

  const backgroundResponsiveImage =
    primary.hero_background_image?.gatsbyImageData &&
    withArtDirection(getImage(primary.hero_background_image.gatsbyImageData)!, [
      {
        media: mediaQueries.desktop,
        image: getImage(
          primary.hero_background_image?.thumbnails.desktop?.gatsbyImageData,
        )!,
      },
      {
        media: mediaQueries.tablet,
        image: getImage(
          primary.hero_background_image?.thumbnails.tablet?.gatsbyImageData,
        )!,
      },
      {
        media: mediaQueries.desktopLarge,
        image: getImage(
          primary.hero_background_image?.thumbnails.desktopLarge
            ?.gatsbyImageData,
        )!,
      },
    ]);

  const handleClick =
    (button: PrismicLandingPageV2DataBodyHerocalltoactionItem, index: number) =>
    () => {
      elementClick(button.button_text.text || "", {
        buttonId: "hero_call_to_action_button_" + index,
        context: {
          prismicPageId: context.prismicPageId,
        },
      });

      redirect(
        context.language,
        button.redirect_on_click_url,
        !!button.requires_authorization,
      );
    };

  return (
    <Styled.Root>
      <Styled.BackgroundImage
        image={backgroundResponsiveImage}
        alt={primary.hero_background_image?.alt || ""}
      />
      <Styled.Gradient />
      <Container>
        <Row>
          <Styled.ContentWrapper>
            <Styled.TextWrapper>
              <Text as="h2" $color="white" $style="bold_32">
                {primary.hero_title.text}
              </Text>
              <Text
                as="p"
                $color="white"
                $style={{ default: "regular_16", largeMobile: "regular_18" }}
              >
                {primary.hero_description.text}
              </Text>
            </Styled.TextWrapper>
            <Styled.ButtonWrapper>
              {items.map((button, index) => {
                return (
                  <Button
                    key={index}
                    variant={
                      button.button_variant === null
                        ? undefined
                        : (button.button_variant as Variant)
                    }
                    onClick={handleClick(button, index)}
                  >
                    {button.button_text.text}
                  </Button>
                );
              })}
            </Styled.ButtonWrapper>
          </Styled.ContentWrapper>
        </Row>
      </Container>
    </Styled.Root>
  );
};
